// Role-less URLs: the router keeps its internal role-prefixed paths
// (/admin/…, /parent/…, /coach/…), but the address bar shows them WITHOUT
// the role word for the signed-in user. A visible path is resolved back to
// the user's own role tree when a matching route exists, otherwise it is
// treated as a global page. Old prefixed links still work (and are cleaned).
import {
  UNSAFE_createBrowserHistory,
  createRoutesFromChildren,
  matchPath,
  parsePath,
  createPath,
} from 'react-router-dom';

const PREFIX = {
  admin: '/admin', staff: '/staff', coach: '/coach',
  parent: '/parent', customer: '/customer', player: '/player',
};
const ROLE_KEY = 'vyRole';
let patterns = [];

export function registerRoutes(fragment) {
  const out = [];
  const walk = (routes, base) => routes.forEach((r) => {
    let p = base;
    if (r.path) p = r.path.startsWith('/') ? r.path : `${base.replace(/\/$/, '')}/${r.path}`;
    if (r.path && r.path !== '*') out.push(p);
    if (r.children) walk(r.children, p);
  });
  walk(createRoutesFromChildren(fragment.props.children), '');
  patterns = out;
}

const matches = (path) => patterns.some((p) => matchPath({ path: p, end: true }, path));

export const getStoredRole = () => {
  try { return localStorage.getItem(ROLE_KEY); } catch { return null; }
};

export const rememberRole = (role) => {
  try {
    if (role) localStorage.setItem(ROLE_KEY, role);
    else localStorage.removeItem(ROLE_KEY);
  } catch { /* storage unavailable */ }
};

// Visible (address-bar) path → internal route path for `role`.
export function resolveForRole(visible, role = getStoredRole()) {
  const P = PREFIX[role];
  if (!P || visible === P || visible.startsWith(`${P}/`)) return visible;
  const candidate = visible === '/' ? P : `${P}${visible}`;
  return matches(candidate) ? candidate : visible;
}

// Internal route path → what the address bar shows for `role`.
export function toVisiblePath(internal, role = getStoredRole()) {
  const P = PREFIX[role];
  if (!P || !(internal === P || internal.startsWith(`${P}/`)) || !matches(internal)) return internal;
  const stripped = internal.slice(P.length) || '/';
  return resolveForRole(stripped, role) === internal ? stripped : internal;
}

// True when a role-less path exists in ANY role tree (deep link before login).
export const isRoleDeepLink = (visible) =>
  visible !== '/' && Object.values(PREFIX).some((P) => matches(`${P}${visible}`));

export const getInternalPathname = () => resolveForRole(window.location.pathname);

function createRoleHistory() {
  const base = UNSAFE_createBrowserHistory({ v5Compat: true });
  const asObj = (to) => (typeof to === 'string' ? parsePath(to) : { ...to });
  const visible = (to) => {
    const t = asObj(to);
    if (t.pathname) t.pathname = toVisiblePath(t.pathname);
    return t;
  };
  let lastBase = null;
  let lastLoc = null;
  const internalize = (loc) => {
    if (loc === lastBase && lastLoc) return lastLoc;
    lastBase = loc;
    lastLoc = { ...loc, pathname: resolveForRole(loc.pathname) };
    return lastLoc;
  };
  return {
    get action() { return base.action; },
    get location() { return internalize(base.location); },
    createHref: (to) => base.createHref(visible(to)),
    createURL: (to) => base.createURL(visible(to)),
    encodeLocation: (to) => base.encodeLocation(to),
    push: (to, state) => base.push(visible(to), state),
    replace: (to, state) => base.replace(visible(to), state),
    go: (n) => base.go(n),
    listen: (fn) => base.listen(({ action, location, delta }) =>
      fn({ action, location: internalize(location), delta })),
    // Clean an old prefixed URL (e.g. /admin/tournaments/…) in place.
    cleanCurrentUrl() {
      const loc = base.location;
      const v = toVisiblePath(resolveForRole(loc.pathname));
      if (v !== loc.pathname) {
        window.history.replaceState(window.history.state, '', createPath({ ...loc, pathname: v }));
      }
    },
  };
}

export const roleHistory = createRoleHistory();
